// Rift Scrabbler (replaces `skitter`) — fast Fractureborn rusher (P1 §2/02, elite §4: Crownclaw Alpha).
// Low-slung, wide four-legged basalt creature: heavy hunched shoulders, hooked bone front claws, a fractured crystal
// spinal ridge with sparse magenta seams and a small forward-pointing skull. Diagonal-pair scramble.
// Wind-up: the front claws rear up and the skull dips into a low attack posture.
import * as THREE from 'three/webgpu';
import { ArtBuilder, ArtGeo, at, EnemyArt, limb, PAL } from './kit';
import { scaleArt, shard, sideways, slab } from './parts_swarm';

type V3 = [number, number, number];
const SH_Y = 0.58, SH_Z = 0.2, HIP_Y = 0.48, HIP_Z = -0.45;
const FRONT_SWING = 0.62, REAR_SWING = 0.75;
const BONE = 0xcfc3a8, HIDE = PAL.basalt, HIDE_L = PAL.basaltLight, RIDGE = 0x8a8494;

/** hooked claw in the YZ plane: grip at the origin, curving forward and down to a point */
function claw(k: number): THREE.BufferGeometry {
  const p: [number, number][] = [[-0.05, 0.04], [0.06, 0.07], [0.17, 0.03], [0.24, -0.06], [0.25, -0.17], [0.2, -0.09], [0.12, -0.03], [0.02, -0.04]];
  return sideways(slab(p.map(([x, y]) => [x * k, y * k] as [number, number]), 0.045 * Math.min(k, 1.3)));
}

function build(elite: boolean): ArtGeo {
  const b = new ArtBuilder();
  const ck = elite ? 1.65 : 1.15; // claw scale

  // ---- body: heavy front, tapering back, short tail
  b.add(at(new THREE.SphereGeometry(1, 10, 7), 0, 0.5, -0.2, -0.1, 0, 0, [0.26, 0.19, 0.55]), { color: HIDE });
  b.add(at(new THREE.SphereGeometry(1, 10, 7), 0, 0.58, 0.16, 0, 0, 0, [0.32, 0.21, 0.26]), { color: HIDE_L }); // shoulder hump
  b.add(limb([0, 0.5, -0.62], [0, 0.42, -0.82], 0.09, 0.02, 6), { color: HIDE });

  // ---- fractured spinal ridge: basalt plates, two magenta seams
  const ridge: [V3, V3, number, boolean][] = [
    [[0, 0.76, 0.2], [0.03, 0.96, 0.1], 0.065, false],
    [[0, 0.74, 0.03], [-0.04, 0.94, -0.09], 0.07, false],
    [[0, 0.68, -0.15], [0.02, 0.85, -0.27], 0.06, false],
    [[0, 0.63, -0.33], [-0.02, 0.76, -0.45], 0.05, false],
    [[0, 0.57, -0.5], [0, 0.66, -0.6], 0.04, false],
  ];
  for (const [a, c, r] of ridge) b.add(shard(a, c, r, 4), { color: RIDGE });
  b.add(shard([0.02, 0.74, 0.12], [0.06, 0.88, 0.04], 0.03, 4), { color: PAL.magenta, glow: true });
  b.add(shard([-0.02, 0.67, -0.22], [-0.07, 0.8, -0.32], 0.028, 4), { color: PAL.magenta, glow: true });
  b.add(shard([0.13, 0.62, -0.1], [0.26, 0.72, -0.16], 0.03, 4), { color: PAL.magenta, glow: true }); // flank seam

  // ---- small forward-pointing skull (dips on wind-up)
  const head = { pivot: [0, 0.56, 0.36] as V3, raise: 0.3 };
  b.add(limb([0, 0.58, 0.32], [0, 0.5, 0.56], 0.12, 0.1, 8), { color: HIDE, ...head }); // neck
  const skull = new THREE.ConeGeometry(0.135, 0.4, 6); skull.rotateX(Math.PI / 2);
  b.add(at(skull, 0, 0.5, 0.74, 0.12, 0, 0, [1, 0.8, 1]), { color: BONE, ...head });
  b.add(at(new THREE.ConeGeometry(0.075, 0.22, 5).rotateX(Math.PI / 2), 0, 0.41, 0.7, 0.35), { color: 0x5a5550, ...head }); // jaw
  for (const s of [-1, 1]) b.add(at(new THREE.OctahedronGeometry(0.03), s * 0.07, 0.545, 0.7), { color: PAL.magenta, glow: true, ...head });
  if (elite) {
    // twin amber crystal horns sweeping up and forward
    for (const s of [-1, 1]) b.add(shard([s * 0.06, 0.56, 0.64], [s * 0.22, 0.9, 0.76], 0.045, 5), { color: PAL.amber, glow: true, ...head });
    for (const s of [-1, 1]) b.add(shard([s * 0.08, 0.56, 0.6], [s * 0.16, 0.7, 0.66], 0.06, 5), { color: PAL.copper, ...head }); // horn sockets
  }

  // ---- legs: diagonal pairs (front-left + rear-right phase 0, the other pair phase π)
  for (const s of [-1, 1]) {
    // front: thick arm with a hooked bone claw, claw tip near the ground ahead of the shoulder
    const fr = { pivot: [s * 0.24, SH_Y, SH_Z] as V3, swing: FRONT_SWING, sphase: s > 0 ? 0 : Math.PI, raise: -0.85 };
    const sh: V3 = [s * 0.24, SH_Y, SH_Z], el: V3 = [s * 0.47, 0.4, 0.16], wr: V3 = [s * 0.47, 0.16, 0.34];
    b.add(limb(sh, el, 0.1, 0.07, 8), { color: HIDE_L, ...fr });
    b.add(limb(el, wr, 0.07, 0.05, 6), { color: HIDE, ...fr });
    // pitch the claw so its tip just touches the ground at rest
    const gy = wr[1] + 0.02, tipA = Math.atan2(-0.17, 0.25), tipR = Math.hypot(0.17, 0.25) * ck;
    b.add(at(claw(ck), wr[0], gy, wr[2] - 0.02, tipA - Math.asin(Math.max(-1, (0.015 - gy) / tipR))), { color: BONE, ...fr });
    b.add(at(new THREE.SphereGeometry(0.05, 6, 4), el[0], el[1], el[2]), { color: HIDE_L, ...fr });
    if (elite) b.add(shard([s * 0.36, 0.5, 0.18], [s * 0.52, 0.64, 0.04], 0.04, 4), { color: PAL.amber, ...fr }); // elbow spur
    // rear: digitigrade hind leg (hip -> knee forward -> hock back -> paw)
    const rr = { pivot: [s * 0.2, HIP_Y, HIP_Z] as V3, swing: REAR_SWING, sphase: s > 0 ? Math.PI : 0 };
    const hp: V3 = [s * 0.2, HIP_Y, HIP_Z], kn: V3 = [s * 0.4, 0.36, -0.3], hk: V3 = [s * 0.43, 0.2, -0.58], pw: V3 = [s * 0.42, 0.03, -0.5];
    b.add(limb(hp, kn, 0.11, 0.07, 8), { color: HIDE_L, ...rr });
    b.add(limb(kn, hk, 0.06, 0.045, 6), { color: HIDE, ...rr });
    b.add(limb(hk, pw, 0.045, 0.04, 6), { color: HIDE, ...rr });
    b.add(at(new THREE.ConeGeometry(0.06, 0.16, 5).rotateX(Math.PI / 2), s * 0.42, 0.03, -0.44, 0.2), { color: BONE, ...rr }); // toe claws
  }
  return scaleArt(b.build(), 0.86); // authored large, scaled to ~1.45 m long / 0.85 m tall
}

export const ART: EnemyArt = { name: 'Rift Scrabbler', build, stride: 5.0, bob: -0.09, outline: 0.03 };
